import { fetchUserTree } from '../services/userTreeService';
import { fetchAuthSession } from 'aws-amplify/auth';
import { apiPost } from '../utils/api';

// Cache variables (in-memory only).
// NOTE: this module used to keep its own localStorage copy of the flattened
// tree ('userTreeCache'). For large trees that setItem always exceeded the
// localStorage quota and silently failed — and it duplicated the (compressed)
// cache that userTreeService now persists. The flattened shape is cheap to
// re-derive from fetchUserTree, so we only cache in memory here.
let cachedUserTree = null;
let cacheTimestamp = null;
const CACHE_DURATION_MS = 5 * 60000; // 5 * 60 seconds cache (default)
const CACHE_DURATION_AGENT_MS = 10 * 1000; // 10 seconds cache for agents/super-agents
const LOCAL_STORAGE_KEY = 'userTreeCache'; // legacy key, only removed now
const LOCAL_STORAGE_TIMESTAMP_KEY = 'userTreeCacheTimestamp';

// Helper to get cache duration based on user type
const getCacheDuration = () => {
  if (cachedUserTree && cachedUserTree.rootId) {
    const rootUser = cachedUserTree.users?.[cachedUserTree.rootId];
    if (rootUser && (rootUser.type === 'super-agent' || rootUser.type === 'agent')) {
      return CACHE_DURATION_AGENT_MS;
    }
  }
  return CACHE_DURATION_MS;
};

// Fetch lock to prevent multiple simultaneous calls
let isFetchingData = false;
let pendingFetchPromise = null;
let fetchCount = 0;

// Force reload function that bypasses cache
export const forceReloadUsers = async () => {
  // Clear the cache
  cachedUserTree = null;
  cacheTimestamp = null;
  localStorage.removeItem(LOCAL_STORAGE_KEY);
  localStorage.removeItem(LOCAL_STORAGE_TIMESTAMP_KEY);
  // Fetch fresh data
  return getUsersData(true);
};

// Clear cache function for logout
export const clearCache = () => {
  cachedUserTree = null;
  cacheTimestamp = null;
  localStorage.removeItem(LOCAL_STORAGE_KEY);
  localStorage.removeItem(LOCAL_STORAGE_TIMESTAMP_KEY);
  
  // Clear any pending requests
  isFetchingData = false;
  pendingFetchPromise = null;
};

export const getUsersData = async (forceReload = false) => {
  // Generate a unique ID for this request for debugging
  const requestId = ++fetchCount;
  
  try {
    const now = Date.now();
    
    // If we have cached data and not forcing reload, return it
    const cacheDuration = getCacheDuration();
    if (cachedUserTree && !forceReload && cacheTimestamp && (now - cacheTimestamp < cacheDuration)) {
      return {
        success: true,
        data: cachedUserTree
      };
    }
    
    // If there's already a fetch in progress, wait for it instead of starting a new one
    if (isFetchingData && pendingFetchPromise) {
      return pendingFetchPromise;
    }
    
    // Set the lock
    isFetchingData = true;
    
    // Create a promise to resolve when the data is fetched
    pendingFetchPromise = (async () => {
      try {
        // Fetch the tree from the Lambda function
        const userTree = await fetchUserTree(forceReload, {
          staleWhileRevalidate: !forceReload
        });
        
        // Transform the tree into a flat structure
        const users = {};
        
        // Helper function to flatten the tree
        const flattenTree = (node) => {
          if (!node) return;

          try {
            // Add this node to the users object
            users[node.username] = {
              id: node.username,
              name: node.name,
              type: node.type,
              username: node.username,
              parentUsername: node.parentUsername,
              creationDate: node.creationDate, // Preserve creation date
              attributes: node.attributes || {}, // Include attributes
              balances: node.balances || {}, // Preserve balances
              children: node.children ? node.children.map(child => 
                typeof child === 'object' ? child.username : child
              ) : []
            };
            
            // Process children - ensure all children are processed
            if (node.children && Array.isArray(node.children)) {
              node.children.forEach(child => {
                if (child && typeof child === 'object') {
                  flattenTree(child);
                } else if (typeof child === 'string') {
                  // Handle case where child is just a username reference
                }
              });
            }
          } catch (error) {
            // Error processing node
          }
        };
        
        // Start flattening from the root
        try {
          flattenTree(userTree);
        } catch (error) {
          throw error;
        }
        
        // Cache the result
        cachedUserTree = {
          users,
          rootId: userTree.username // The authenticated user is our root
        };
        cacheTimestamp = Date.now();
        
        return {
          success: true,
          data: cachedUserTree
        };
      } finally {
        // Release the lock once we're done, regardless of success or failure
        isFetchingData = false;
        pendingFetchPromise = null;
      }
    })();
    
    return pendingFetchPromise;
  } catch (error) {
    // Log detailed error information
    console.error(`[getUsersData] Request ${requestId} failed:`, {
      message: error.message,
      type: error.name,
      statusCode: error.response?.status,
      hasCachedData: !!cachedUserTree
    });
    
    // If we have cached data, use it as fallback
    if (cachedUserTree) {
      console.warn(`[getUsersData] Using cached data as fallback after error`);
      return {
        success: true,
        data: cachedUserTree,
        fromCache: true,
        error: error.message // Include error for debugging
      };
    }
    
    return {
      success: false,
      error: error.message || 'Failed to fetch user data'
    };
  }
};

// Helper function to build tree structure from flat data
export const buildTree = async (data, nodeId) => {
  try {
    const node = data.users[nodeId];
    if (!node) {
      return null;
    }
    
    const result = { 
      ...node,
      creationDate: node.creationDate, // Preserve creation date
      attributes: node.attributes || {}, // Preserve attributes if they exist
      balances: node.balances || {} // Preserve balances if they exist
    };
    
    // Only include children that exist in the data
    if (result.children && result.children.length > 0) {
      const childPromises = result.children.map(async (childId) => {
        // Skip invalid child IDs
        if (!childId || typeof childId !== 'string') {
          return null;
        }
        
        const child = data.users[childId];
        if (child) {
          return buildTree(data, childId);
        } else {
          return null;
        }
      });
      
      result.children = await Promise.all(childPromises);
      result.children = result.children.filter(Boolean);
    } else {
      result.children = [];
    }
    
    return result;
  } catch (error) {
    return null;
  }
};

// Helper function to search users
export const searchUsers = async (query) => {
  try {
    if (!query || query.length < 3) {
      return [];
    }
    
    const { success, data } = await getUsersData();
    if (!success || !data || !data.users) {
      return [];
    }
    
    const lowercaseQuery = query.toLowerCase();
    
    // Collect matching users, with special attention to players
    const matchingUsers = Object.values(data.users)
      .filter(user => {
        if (!user) return false;
        
        const name = user.name || '';
        const username = user.username || '';
        
        // Check if this user matches the search term
        const matches = name.toLowerCase().includes(lowercaseQuery) ||
                     username.toLowerCase().includes(lowercaseQuery);
        
        return matches;
      });
    
    // Build complete tree for each matching user
    const results = await Promise.all(
      matchingUsers.map(async (user) => {
        try {
          if (!user || !user.id) {
            return null;
          }
          const fullNode = await buildTree(data, user.id);
          return fullNode;
        } catch (error) {
          return null;
        }
      })
    );
    
    return results.filter(Boolean);
  } catch (error) {
    return [];
  }
};

// Get all available currencies in the system
export const getAvailableCurrencies = async () => {
  try {
    const { success, data } = await getUsersData();
    if (!success) return [];
    
    // Set to store unique currencies
    const currencies = new Set();
    
    // Iterate through all users to find currencies
    Object.values(data.users).forEach(user => {
      if (user.balances) {
        // Add all currencies from this user's balances
        Object.keys(user.balances).forEach(currency => currencies.add(currency));
      }
    });
    
    // Convert Set to Array and sort alphabetically
    return Array.from(currencies).sort();
  } catch (error) {
    return [];
  }
};

// Process a transaction between users
export const processTransaction = async (endUser, currency, amount, isDeposit, notes, isTestTransaction = false) => {
  try {
    if (!endUser || !currency || !amount) {
      throw new Error('Missing required transaction parameters');
    }

    // Ensure amount is a number
    const numericAmount = Number(amount);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      throw new Error('Amount must be a positive number');
    }

    // Get the current auth session to include the token
    const { tokens } = await fetchAuthSession();
    if (!tokens?.idToken) {
      throw new Error('Authentication required. Please log in again.');
    }

    try {
      const response = await apiPost(
        'coretransact',
        '/core/transact',
        {
          targetAgent: endUser.username,
          isWithdrawal: !isDeposit,
          amount: numericAmount,
          currency,
          notes,
          isTestTransaction
        },
        tokens.idToken.toString()
      );
      
      return response;
      
    } catch (apiError) {
      return {
        error: apiError.message || 'Transaction failed'
      };
    }
  } catch (error) {
    return {
      error: error.message || 'Transaction failed'
    };
  }
}; 